import React, { memo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CallLog,
  CallDirection,
  DialerCallSourceEnum,
} from '../../../../../types/call-logs';
import './cells.scss';
import { MissedCallIcon } from '../../../../../../../shared/svg/missed-call';
import { IncomingCallIcon } from '../../../../../../../shared/svg/incoming-call';
import { OutgoingCallIcon } from '../../../../../../../shared/svg/outgoing-call';
import {
  ArrowForwardUp,
  Cross,
  VoiceMailIcon,
} from '../../../../../../../shared/svg';
import { DialerCall } from '../../../../../../../shared/svg/dialer-call';
import Button from '../../../../../../../shared/design-system/ui/button';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../shared/utils';
import { setSelectedCallbackProspect } from '../../../../../dialer-slice';
import AudioPlayer from '../../../../audio-player';
import { CallManagementState } from '../../../../../../../shared/components/dialer/types';
import { isOngoingDialerCall } from '../../../../../../../shared/components/dialer/constants';
import { getSignedUrlForCallRecordingRequest } from '../../../../../extra-actions';
import { RootState } from '../../../../../../../store/root-reducer';
import TruncatedTextWithAutoTooltip from '../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import CopyButton from '../../../../../../../shared/components/copy-button';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';

interface IProps {
  row: CallLog;
  isShowVoicemailPopover: boolean;
  setShowVoicemailPopover: React.Dispatch<React.SetStateAction<number | null>>;
  currentCallManagementState: CallManagementState;
  activeCallLogsTab?: string;
  onProspectNameClick?: (ctx: { columnId: number; row: CallLog }) => void;
  onMarkAsSeen?: (callId: number) => void;
}

const ProspectNameCell: React.FC<IProps> = memo(
  ({
    row,
    isShowVoicemailPopover,
    setShowVoicemailPopover,
    currentCallManagementState,
    activeCallLogsTab,
    onProspectNameClick,
    onMarkAsSeen,
  }) => {
    const dispatch = useDispatch();
    const [showTranscript, setShowTranscript] = useState(false);
    const getSignedUrlForCallRecordingResponse = useSelector(
      (state: RootState) => state?.dialer?.getSignedUrlForCallRecordingResponse,
    );
    const myPhoneNumbersList = useSelector(
      (state: RootState) => state?.dialer?.myPhoneNumbersList,
    );
    const isOngoingCall = isOngoingDialerCall({
      callIsInprogress: currentCallManagementState?.callIsInprogress,
      callStatus: currentCallManagementState?.callStatus,
    });
    const voicemailPopoverContentRef = useRef<HTMLDivElement | null>(null);
    // get the call forwarded to user name or number
    const callForwardedTo = row?.attendantUser
      ? `${row?.attendantUser?.firstName} ${row?.attendantUser?.lastName}`
      : row?.forwardedNumber?.number;

    const callForwardedFrom = row?.recipientUser
      ? `${row?.recipientUser?.firstName} ${row?.recipientUser?.lastName}`
      : row?.fromNumber;

    // tooltips for forward calls
    const forwardTooltipText =
      row?.isDirectCall === true
        ? `Call Forwarded to ${callForwardedTo}`
        : `Call Forwarded from ${callForwardedFrom}`;

    const handleCallbackClick = useCallback(() => {
      dispatch(
        setSelectedCallbackProspect([
          { ...row, callSource: DialerCallSourceEnum.Manual },
        ]),
      );
    }, [row]);

    // Add null check after hooks
    if (!row || !row.prospectName) {
      return <span>-</span>;
    }
    const missedCall = row?.callDirection === CallDirection.Missed;

    const getIcon = () => {
      switch (row.callDirection) {
        case CallDirection.Outgoing:
          return (
            <OutgoingCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon"
            />
          );
        case CallDirection.Incoming:
          return (
            <IncomingCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon"
            />
          );
        case CallDirection.Missed:
          return (
            <MissedCallIcon
              width={20}
              height={20}
              className="prospect-name-cell__left__icon prospect-name-cell__left__icon--missed"
            />
          );
        default:
          return null;
      }
    };

    const textClassName =
      row.callDirection === CallDirection.Missed
        ? 'prospect-name-cell__left__text prospect-name-cell__left__text--missed'
        : 'prospect-name-cell__left__text';
    return (
      <div
        className={`prospect-name-cell ${
          missedCall ? 'prospect-name-cell-missed' : ''
        }`}
      >
        <div className="prospect-name-cell__left">
          {getIcon()}
          <span
            className={textClassName}
            {...accessibleOnClick(() =>
              onProspectNameClick?.({ columnId: row?.id, row }),
            )}
          >
            <TruncatedTextWithAutoTooltip
              text={row?.prospectName}
              className={textClassName}
            />
          </span>
          {row?.isForwarded === true && (
            <OverlayTooltip text={forwardTooltipText}>
              <span className="prospect-name-cell__left__arrow-forward-up">
                <ArrowForwardUp width={16} height={16} />
              </span>
            </OverlayTooltip>
          )}
          {missedCall && !row?.isSeen && (
            <span className="prospect-name-cell__left__dot" />
          )}
        </div>
        <div className="prospect-name-cell__right">
          {missedCall && activeCallLogsTab !== 'voicemails' && (
            <div>
              {row?.isRecordingPresent === true ? (
                // VoiceMailIcon button enabled
                <OverlayPopover
                  show={isShowVoicemailPopover}
                  onToggle={(open) => {
                    if (open) {
                      setShowVoicemailPopover(row?.id);
                    } else {
                      setShowVoicemailPopover((current) =>
                        current === row?.id ? null : current,
                      );
                    }
                  }}
                  rePositionOnChange={true}
                  popperConfig={{
                    modifiers: [
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['bottom'],
                          allowedAutoPlacements: ['top', 'bottom'],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          padding: 15,
                          altAxis: true,
                          rootBoundary: 'viewport',
                        },
                      },
                    ],
                  }}
                  content={
                    <div
                      ref={voicemailPopoverContentRef}
                      className="prospect-name-cell__voicemail-popover"
                      {...accessibleOnClick((e) => {
                        e.stopPropagation();
                      })}
                    >
                      <div className="prospect-name-cell__voicemail-popover__header">
                        <span className="prospect-name-cell__voicemail-popover__header__title">
                          Voicemail
                        </span>
                        <div
                          className="cross-icon-container"
                          {...accessibleOnClick((e) => {
                            e.stopPropagation();
                            setShowVoicemailPopover(null);
                          })}
                        >
                          <Cross width={16} height={16} />
                        </div>
                      </div>
                      <AudioPlayer
                        audioSrc={`${
                          ['ringing', 'answered']?.includes(
                            currentCallManagementState?.callStatus || '',
                          )
                            ? ''
                            : getSignedUrlForCallRecordingResponse?.signedUrl
                        }`}
                        className="voicemail-greeting-audio-player"
                        onRequestClose={() => setShowVoicemailPopover(null)}
                        closeBoundaryRef={voicemailPopoverContentRef}
                      />
                      <div
                        className={`prospect-name-cell__voicemail-popover__footer ${
                          !row?.transcription ? 'disabled' : ''
                        }`}
                      >
                        {showTranscript && row?.transcription ? (
                          <>
                            <div className="prospect-name-cell__voicemail-popover__footer__transcript">
                              {row?.transcription ? (
                                row?.transcription
                              ) : (
                                <OverlayTooltip text="Transcription in progress...">
                                  <span>{row?.transcription}</span>
                                </OverlayTooltip>
                              )}
                            </div>
                            <div className="prospect-name-cell__voicemail-popover__footer__transcript__copy-icon">
                              <CopyButton
                                text={row?.transcription}
                                copyTooltipText="Copy"
                                resetDelay={1000}
                                placement={Placement.Bottom}
                                className="voicemail-copy-button-icon"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            {row?.isTranscriptionInProgress ? (
                              <span className="transcript-cell__text-placeholder">
                                Transcription in progress...
                              </span>
                            ) : (
                              <span
                                {...accessibleOnClick(() =>
                                  setShowTranscript(true),
                                )}
                                className="prospect-name-cell__voicemail-popover__footer__view-transcript-text"
                              >
                                View Transcript
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  }
                  placement={Placement.RightStart}
                  className="prospect-voicemail-popover-container"
                >
                  <OverlayTooltip text="Listen Voicemail">
                    <Button
                      className={
                        isShowVoicemailPopover
                          ? 'prospect-name-cell__voicemail-button--active'
                          : ' '
                      }
                      {...accessibleOnClick((e) => {
                        e.stopPropagation();
                        setShowVoicemailPopover(
                          isShowVoicemailPopover ? null : row?.id,
                        );
                        dispatch(
                          getSignedUrlForCallRecordingRequest(
                            Number(row?.callId),
                          ),
                        );
                        if (missedCall && !row?.isSeen) {
                          onMarkAsSeen?.(row.id);
                        }
                      })}
                    >
                      <VoiceMailIcon width={16} height={16} />
                    </Button>
                  </OverlayTooltip>
                </OverlayPopover>
              ) : (
                // VoiceMailIcon button disabled
                <OverlayTooltip text="No voicemail available">
                  <Button disabled>
                    <VoiceMailIcon width={16} height={16} />
                  </Button>
                </OverlayTooltip>
              )}
            </div>
          )}

          {/* Callback button */}
          {hasPermission(Permissions.PHONE_CALL_WRITE) &&
            (isOngoingCall ? (
              <OverlayTooltip
                text="Another call is already in progress"
                placement={Placement.Top}
                className="cell-card__tooltip"
              >
                <Button disabled>
                  <DialerCall width={16} height={16} />
                </Button>
              </OverlayTooltip>
            ) : myPhoneNumbersList?.total === 0 ? (
              <OverlayTooltip text="No phone number available. Please purchase or get one assigned to start calling.">
                <Button
                  {...accessibleOnClick(() => handleCallbackClick())}
                  disabled={myPhoneNumbersList?.total === 0}
                >
                  <DialerCall width={16} height={16} />
                </Button>
              </OverlayTooltip>
            ) : (
              <OverlayTooltip text="Callback">
                <Button
                  {...accessibleOnClick((e) => {
                    e.stopPropagation();
                    handleCallbackClick();
                  })}
                >
                  <DialerCall width={16} height={16} />
                </Button>
              </OverlayTooltip>
            ))}
        </div>
      </div>
    );
  },
);

ProspectNameCell.displayName = 'ProspectNameCell';

export default ProspectNameCell;
